import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { apiClient } from '../lib/apiClient';

const API = import.meta.env.VITE_API_URL ?? '';

/**
 * Public Share View — renders the shared object for external recipients.
 * No authentication required. Accessed via /s/{token}.
 */
export default function PublicShareView({ shareToken }) {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [actionDone, setActionDone] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API}/api/v1/aif/public/share/view/${shareToken}`);
        if (res.status === 410) { setError('expired'); return; }
        if (res.status === 403) { setError('revoked'); return; }
        if (!res.ok) { setError('notfound'); return; }
        setData(await res.json());
      } catch {
        setError('network');
      } finally {
        setLoading(false);
      }
    })();
  }, [shareToken]);

  const handleAction = async (action) => {
    try {
      await apiClient.post(`/api/v1/aif/public/share/view/${shareToken}/action`, { action }, { queueOnOffline: false });
      setActionDone(action);
    } catch { /* ApiError */ }
  };

  // Upgrade CTA
  const [ctaLoading, setCtaLoading] = useState(false);

  const handleUpgradeCTA = async () => {
    setCtaLoading(true);
    try {
      const res = await apiClient.post('/api/v1/aif/public/share/upgrade-cta', {
        share_token: shareToken,
        email: null,
      });
      if (res?.data?.signup_url) {
        window.location.href = res.data.signup_url;
      }
    } catch {
      // Fallback: navigate directly
      window.location.href = `/signup?from_share=${shareToken}`;
    }
    setCtaLoading(false);
  };

  // Buyer delivery confirmation
  const [confirmForm, setConfirmForm] = useState({ quantity: '', moisture: '', ticket: '' });
  const [confirming, setConfirming] = useState(false);

  const handleConfirmDelivery = async () => {
    if (!confirmForm.quantity) return;
    setConfirming(true);
    try {
      await apiClient.post(`/api/v1/aif/public/share/view/${shareToken}/confirm-delivery`, {
        confirmed_quantity: parseFloat(confirmForm.quantity),
        moisture_pct: confirmForm.moisture ? parseFloat(confirmForm.moisture) : undefined,
        weighbridge_ticket: confirmForm.ticket || undefined,
      }, { queueOnOffline: false });
      setActionDone('confirm_delivery');
    } catch { /* ApiError */ }
    setConfirming(false);
  };

  if (loading) {
    return (
      <div className="min-h-[100dvh] flex items-center justify-center bg-[var(--aif-bg-tertiary)]">
        <div className="w-8 h-8 border-3 border-gray-200 border-t-green-600 rounded-full animate-spin" />
      </div>
    );
  }

  if (error) {
    const msgs = {
      expired: t('share.publicExpired'),
      revoked: t('share.publicRevoked'),
      notfound: t('share.publicNotFound'),
      network: t('app.errorNetwork'),
    };
    return (
      <div className="min-h-[100dvh] flex items-center justify-center bg-[var(--aif-bg-tertiary)] px-6">
        <div className="text-center space-y-3">
          <div className="text-4xl">{error === 'expired' ? '\u23F0' : '\u26D4'}</div>
          <p className="text-sm text-[var(--aif-text-secondary)]">{msgs[error]}</p>
        </div>
      </div>
    );
  }

  if (!data) return null;

  const obj = data.object_data || {};
  const perms = data.permissions || [];
  const canConfirm = perms.includes('confirm');
  const canComplete = perms.includes('complete');
  const isTask = data.share_type === 'task';
  const isDelivery = data.share_type === 'delivery';
  const isDeliveryPoint = data.share_type === 'delivery_point';
  const isProcurement = data.share_type === 'procurement';

  // Google Maps link
  const lat = obj.gps_lat || obj.origin_lat;
  const lon = obj.gps_lon || obj.origin_lon;
  const mapsUrl = lat && lon ? `https://www.google.com/maps/search/?api=1&query=${lat},${lon}` : null;
  const destMapsUrl = obj.destination_lat && obj.destination_lon
    ? `https://www.google.com/maps/search/?api=1&query=${obj.destination_lat},${obj.destination_lon}` : null;

  return (
    <div className="min-h-[100dvh] bg-[var(--aif-bg-tertiary)] flex flex-col">
      {/* Header */}
      <div className="bg-green-700 text-white px-6 py-4">
        <div className="text-lg font-bold flex items-center gap-2">
          <span>{'\uD83C\uDF3E'}</span> AI Farm
        </div>
        <div className="text-sm opacity-90 mt-0.5">{data.object_label}</div>
      </div>

      {/* Upgrade CTA Banner */}
      <div className="bg-blue-50 border-b border-blue-200 px-6 py-3 flex items-center justify-between gap-3" data-testid="upgrade-cta-banner">
        <span className="text-sm text-blue-800">
          {t('share.upgradeCTA', { defaultValue: 'Create your free account to keep access' })}
        </span>
        <button
          onClick={handleUpgradeCTA}
          disabled={ctaLoading}
          className="px-4 py-2 rounded-lg bg-blue-600 text-white text-sm font-medium whitespace-nowrap disabled:opacity-50"
          data-testid="upgrade-cta-button"
        >
          {ctaLoading ? '...' : t('share.signUp', { defaultValue: 'Sign up free' })}
        </button>
      </div>

      {/* Content */}
      <div className="flex-1 px-6 py-5 space-y-4">
        {/* Object Details */}
        {isTask && obj.title && (
          <div className="space-y-3">
            <h2 className="text-lg font-semibold text-[var(--aif-text-primary)]">{obj.title}</h2>
            {obj.due_date && <div className="flex items-center gap-2 text-sm text-[var(--aif-text-secondary)]"><span>{'\uD83D\uDCC5'}</span> {new Date(obj.due_date).toLocaleDateString('de-DE')}</div>}
            {mapsUrl && (
              <a href={mapsUrl} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-sm text-blue-600 hover:underline">
                <span>{'\uD83D\uDCCD'}</span> {t('share.openMaps')}
              </a>
            )}
            {obj.product_name && <div className="flex items-center gap-2 text-sm text-[var(--aif-text-secondary)]"><span>{'\uD83C\uDF3F'}</span> {obj.product_name}{obj.planned_quantity ? ` \u00B7 ${obj.planned_quantity} ${obj.planned_unit || 'kg/ha'}` : ''}</div>}
            {obj.weather_snapshot && (
              <div className="bg-[var(--aif-bg-surface)] rounded-lg p-3 border text-sm text-[var(--aif-text-secondary)]">
                <div className="font-medium mb-1">{t('share.weather')}</div>
                {obj.weather_snapshot.temp && <span>{obj.weather_snapshot.temp}\u00B0C</span>}
                {obj.weather_snapshot.wind && <span> &middot; Wind {obj.weather_snapshot.wind} m/s</span>}
              </div>
            )}
          </div>
        )}

        {isDelivery && (
          <div className="space-y-3">
            <h2 className="text-lg font-semibold text-[var(--aif-text-primary)]">{obj.product_name}</h2>
            <div className="text-sm text-[var(--aif-text-secondary)]">{obj.quantity} {obj.unit}</div>

            {obj.origin_name && (
              <div className="bg-[var(--aif-bg-surface)] rounded-lg p-3 border space-y-1">
                <div className="text-xs font-medium text-[var(--aif-text-muted)]">{t('delivery.pickup')}</div>
                <div className="text-sm text-[var(--aif-text-primary)]">{obj.origin_name}</div>
                {mapsUrl && <a href={mapsUrl} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-600">{t('share.startNavigation')}</a>}
              </div>
            )}

            {obj.destination_name && (
              <div className="bg-[var(--aif-bg-surface)] rounded-lg p-3 border space-y-1">
                <div className="text-xs font-medium text-[var(--aif-text-muted)]">{t('delivery.deliverTo')}</div>
                <div className="text-sm text-[var(--aif-text-primary)]">{obj.destination_name}</div>
                {destMapsUrl && <a href={destMapsUrl} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-600">{t('share.startNavigation')}</a>}
              </div>
            )}
          </div>
        )}

        {/* Message */}
        {data.message && (
          <div className="bg-[var(--aif-bg-surface)] rounded-lg p-3 border">
            <div className="text-xs font-medium text-[var(--aif-text-muted)] mb-1">{t('share.messageFrom')}</div>
            <div className="text-sm text-[var(--aif-text-secondary)]">{data.message}</div>
          </div>
        )}

        {/* Action buttons */}
        {actionDone ? (
          <div className="bg-green-50 border border-green-200 rounded-xl p-4 text-center">
            <div className="text-2xl mb-2">{'\u2705'}</div>
            <p className="text-sm text-green-700">
              {actionDone === 'accept' ? t('share.accepted') :
               actionDone === 'decline' ? t('share.declined') :
               actionDone === 'complete' ? t('share.completed') :
               actionDone === 'arrived' ? t('share.arrived') :
               actionDone === 'confirm_delivery' ? t('share.deliveryConfirmed') :
               t('share.actionDone')}
            </p>
          </div>
        ) : data.response_status !== 'pending' ? (
          <div className="bg-[var(--aif-bg-tertiary)] border rounded-xl p-4 text-center">
            <p className="text-sm text-[var(--aif-text-secondary)]">{t('share.alreadyResponded')}: {data.response_status}</p>
          </div>
        ) : (
          <div className="space-y-2">
            {/* Task actions */}
            {isTask && canConfirm && (
              <div className="flex gap-2">
                <button onClick={() => handleAction('accept')} className="flex-1 py-3 rounded-xl bg-green-600 text-white font-medium text-sm">
                  {'\u2705'} {t('share.acceptTask')}
                </button>
                <button onClick={() => handleAction('decline')} className="flex-1 py-3 rounded-xl bg-red-50 text-red-600 border border-red-200 font-medium text-sm">
                  {'\u274C'} {t('share.declineTask')}
                </button>
              </div>
            )}

            {/* Delivery driver actions */}
            {isDelivery && canComplete && (
              <div className="flex gap-2">
                <button onClick={() => handleAction('accept')} className="flex-1 py-3 rounded-xl bg-blue-600 text-white font-medium text-sm">
                  {'\uD83D\uDCE6'} {t('delivery.startLoading')}
                </button>
                <button onClick={() => handleAction('arrived')} className="flex-1 py-3 rounded-xl bg-green-600 text-white font-medium text-sm">
                  {'\u2705'} {t('delivery.arrived')}
                </button>
              </div>
            )}

            {/* Buyer confirmation form */}
            {isDelivery && canConfirm && (
              <div className="bg-[var(--aif-bg-surface)] rounded-lg p-4 border space-y-3">
                <h3 className="text-sm font-semibold text-[var(--aif-text-primary)]">{t('delivery.confirmDelivery')}</h3>
                <input
                  type="number"
                  value={confirmForm.quantity}
                  onChange={e => setConfirmForm(f => ({ ...f, quantity: e.target.value }))}
                  placeholder={`${t('delivery.actualQuantity')} (${obj.unit || 'kg'})`}
                  className="w-full px-3 py-2 rounded-lg border text-sm"
                />
                <input
                  type="number"
                  step="0.1"
                  value={confirmForm.moisture}
                  onChange={e => setConfirmForm(f => ({ ...f, moisture: e.target.value }))}
                  placeholder={t('delivery.moisture') + ' (%)'}
                  className="w-full px-3 py-2 rounded-lg border text-sm"
                />
                <input
                  value={confirmForm.ticket}
                  onChange={e => setConfirmForm(f => ({ ...f, ticket: e.target.value }))}
                  placeholder={t('delivery.weighbridgeTicket')}
                  className="w-full px-3 py-2 rounded-lg border text-sm"
                />
                <button
                  onClick={handleConfirmDelivery}
                  disabled={confirming || !confirmForm.quantity}
                  className="w-full py-3 rounded-xl bg-green-600 text-white font-medium text-sm disabled:opacity-50"
                >
                  {confirming ? '...' : `\u2705 ${t('delivery.confirmDelivery')}`}
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="px-6 py-3 text-center text-xs text-[var(--aif-text-tertiary)] border-t">
        {t('share.validUntil')}: {new Date(data.expires_at).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
        <div className="mt-1">Powered by AI Farm</div>
      </div>
    </div>
  );
}
